import React, { FC, useEffect, useRef, useState } from 'react';
import { Form, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { FieldError, FieldErrorsImpl, Merge, UseFormRegister, UseFormSetValue, UseFormWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { BiSolidImageAdd, BiX } from 'react-icons/bi';
import { toast } from 'react-toastify';

import { PSInput } from '@/components/ui/input';
import { WrappedErrorBoundary } from '@/pages/WrappedErrorBoundary';

import { useAppDispatch, useAppSelector } from '../../app/hook';
import { useUploadToCDNMutation } from '../../app/services/chatbot-rtk.services';
import ProBadge from '../../components/ui/ProBadge';
import { AlertsType } from '../../constants/alerts.constant';
import { selectUserProfile } from '../auth/auth.slice';
import { handleUpdateAppearanceItem, selectAppearanceState } from './appearanceSlice';
import { IAiAppearance } from './types';

type Props = {
  hasPermission: boolean;
  isInvalid: boolean;
  errors?:
    | Merge<
        FieldError,
        FieldErrorsImpl<{
          companyName: string;
          companyLogo: string;
          companyUrl: string;
        }>
      >
    | undefined;
  watch: UseFormWatch<IAiAppearance>;
  setFormValue: UseFormSetValue<IAiAppearance>;
  register: UseFormRegister<IAiAppearance>;
};

const AppearanceCustomPoweredBy: FC<Props> = ({ hasPermission, isInvalid, errors, watch, setFormValue, register }) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const appearance = useAppSelector(selectAppearanceState);

  const profile = useAppSelector(selectUserProfile);

  const user_identification = profile?.user_identification;

  const [cdnUploader, cdnUploaderResults] = useUploadToCDNMutation();

  const [isCustomPowered, setIsCustomPowered] = useState((appearance?.configs as any)?.customPoweredBy?.isActive ? true : false);

  useEffect(() => {
    setFormValue('customPoweredBy', {
      isActive: isCustomPowered,
      config: {
        companyName: watch('customPoweredBy.config.companyName') ?? '',
        companyLogo: watch('customPoweredBy.config.companyLogo') ?? '',
        companyUrl: watch('customPoweredBy.config.companyUrl') ?? '',
      },
    });

    dispatch(
      handleUpdateAppearanceItem({
        key: 'customPoweredBy',
        value: {
          isActive: isCustomPowered,
          config: {
            companyName: watch('customPoweredBy.config.companyName') ?? '',
            companyLogo: watch('customPoweredBy.config.companyLogo') ?? '',
            companyUrl: watch('customPoweredBy.config.companyUrl') ?? '',
          },
        },
      })
    );
  }, [isCustomPowered]);

  const [logoByUser, setLogoByUser] = useState<string>('');

  const logoRef = useRef<HTMLInputElement | null>(null);

  const handleUploadFileToCdn = async (file: File) => {
    try {
      const formData = new FormData();
      formData.append('files', file, user_identification + '_' + file.name);
      const result = await cdnUploader(formData).unwrap();

      return result[0].cdnPath;
    } catch (err: any) {
      toast.error(err.data.message || err.message);
    }
  };

  const handleSelectCustomLogo = () => {
    if (logoRef.current) {
      logoRef.current.value = '';
      logoRef.current.click();
    }
  };

  const handleVerifyAndSelectCustomLogo = (e: any) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];

      if (file.name.length > 150) {
        toast.error(t?.(AlertsType.ISSTKO007, { chars: 150 }));
        return false;
      }

      if (file.size / (1024 * 1024) > 2) {
        toast.error(t(AlertsType.ISSTKO008, { size: 2 }));
        return false;
      }

      if (!['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/svg+xml'].includes(file.type)) {
        toast.error(t(AlertsType.ISSTKO009));
        return;
      }

      handleUploadFileToCdn(file).then(cdnPath => {
        setLogoByUser(cdnPath);
        setFormValue('customPoweredBy.config.companyLogo', cdnPath);
        dispatch(
          handleUpdateAppearanceItem({
            key: 'customPoweredBy',
            value: {
              isActive: true,
              config: {
                companyName: watch('customPoweredBy.config.companyName') ?? '',
                companyLogo: cdnPath,
                companyUrl: watch('customPoweredBy.config.companyUrl') ?? '',
              },
            },
          })
        );
      });
    }
  };

  const handleRemoveClientLogo = () => {
    setLogoByUser('');
    setFormValue('customPoweredBy.config.companyLogo', '');
  };

  return (
    <div className="tw-flex tw-items-center tw-flex-wrap tw-gap-2 tw-w-full">
      <Form.Group className="tw-flex tw-items-center tw-gap-2">
        <Form.Check
          checked={isCustomPowered}
          disabled={!hasPermission}
          type="switch"
          id="poweredBy"
          className="tw-flex tw-items-center tw-gap-2 tw-text-gray-700 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight"
          label={t('DashboardSettingCustomizeChat/l-03')}
          onChange={e => {
            setIsCustomPowered(e.target.checked);
          }}
        />
        {!hasPermission && <ProBadge className="tw-ms-2" />}
      </Form.Group>

      {isCustomPowered && (
        <React.Fragment>
          <div className="tw-w-full tw-flex tw-items-center tw-gap-2">
            {logoByUser && (
              <Form.Group>
                <Form.Label className="tw-text-gray-500 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">
                  {t('AppearanceCustomPoweredBy/key1')}
                </Form.Label>
                <div className="tw-w-10 tw-h-10 tw-cursor-pointer tw-rounded-full tw-border-4 tw-relative tw-border-solid tw-flex tw-flex-col tw-items-center tw-justify-center">
                  <BiX
                    size={22}
                    className="tw-text-gray-100 tw-absolute tw-right-[-5px] tw-top-[-10px] tw-cursor-pointer tw-bg-black tw-rounded-full tw-z-10"
                    onClick={handleRemoveClientLogo}
                  />
                  <img className="tw-w-full tw-h-9 tw-rounded-full tw-object-contain" src={logoByUser.toString()} alt="" />
                </div>
              </Form.Group>
            )}
            {!logoByUser && (
              <Form.Group>
                <Form.Label className="tw-text-gray-500 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">
                  {t('AppearanceCustomPoweredBy/key1')}
                </Form.Label>
                <OverlayTrigger overlay={<Tooltip>{t('AppearanceCustomPoweredBy/key2')}</Tooltip>}>
                  <button
                    id="btn-select-custom-logo"
                    type="button"
                    className="tw-pl-3 tw-pr-4 tw-py-2 tw-bg-white tw-rounded-[99px] tw-border tw-border-solid tw-border-gray-300 tw-flex tw-justify-start tw-items-center tw-gap-2 tw-text-gray-700 tw-text-base tw-font-normal tw-font-['Lexend'] tw-leading-normal"
                    onClick={handleSelectCustomLogo}
                    disabled={cdnUploaderResults.isLoading}
                  >
                    <BiSolidImageAdd size={22} className="tw-text-gray-400" />
                    {cdnUploaderResults.isLoading ? t('AppearanceCustomPoweredBy/key3') : t('AppearanceCustomPoweredBy/key4')}
                  </button>
                </OverlayTrigger>
                <PSInput
                  type="file"
                  hidden
                  ref={logoRef}
                  accept="image/png, image/jpeg, image/jpg, image/gif, image/svg+xml"
                  onChange={handleVerifyAndSelectCustomLogo}
                />
              </Form.Group>
            )}
          </div>
          <div className="tw-w-full tw-flex tw-flex-col tw-gap-2">
            <Form.Group>
              <Form.Label className="tw-text-gray-500 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">
                {t('AppearanceCustomPoweredBy/key5')}
              </Form.Label>

              <Form.Control
                required
                placeholder={t('AppearanceCustomPoweredBy/key6')}
                type="text"
                className="tw-max-w-60 tw-w-full tw-rounded-md tw-mb-0"
                maxLength={15}
                {...register('customPoweredBy.config.companyName', {
                  onChange(event) {
                    dispatch(
                      handleUpdateAppearanceItem({
                        key: 'customPoweredBy',
                        value: {
                          isActive: true,
                          config: {
                            companyName: event.target.value,
                            companyLogo: watch('customPoweredBy.config.companyLogo') ?? '',
                            companyUrl: watch('customPoweredBy.config.companyUrl') ?? '',
                          },
                        },
                      })
                    );
                  },
                })}
              />
              <Form.Control.Feedback type="invalid">{errors?.companyName?.message}</Form.Control.Feedback>
            </Form.Group>

            <Form.Group>
              <Form.Label className="tw-text-gray-500 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">
                {t('AppearanceCustomPoweredBy/key7')}
              </Form.Label>

              <Form.Control
                placeholder="https://louisebot.com"
                type="url"
                className="tw-max-w-96 tw-w-full tw-rounded-md tw-mb-0"
                isInvalid={!!errors?.companyUrl}
                {...register('customPoweredBy.config.companyUrl', {
                  onChange(event) {
                    dispatch(
                      handleUpdateAppearanceItem({
                        key: 'customPoweredBy',
                        value: {
                          isActive: true,
                          config: {
                            companyName: watch('customPoweredBy.config.companyName') ?? '',
                            companyLogo: watch('customPoweredBy.config.companyLogo') ?? '',
                            companyUrl: event?.target?.value ?? '',
                          },
                        },
                      })
                    );
                  },
                })}
              />
              <Form.Control.Feedback type="invalid">{errors?.companyUrl?.message}</Form.Control.Feedback>
            </Form.Group>
          </div>
        </React.Fragment>
      )}
    </div>
  );
};

export default WrappedErrorBoundary(AppearanceCustomPoweredBy);
